import { useState } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, ShoppingCart, Loader2 } from 'lucide-react'
import Button from '../components/ui/Button'
import SEO from '../components/SEO'
import { usePaystack } from '../hooks/usePaystack'
import {
  validateEmail, validateName, validateSchoolName,
  validatePhone, validateFreeText, validateQuantity,
  sanitize, LIMITS,
} from '../utils/validate'

function FieldError({ msg }) {
  if (!msg) return null
  return <p className="text-xs mt-1" style={{ color: 'var(--warm-strong)' }}>{msg}</p>
}

export default function ProductCheckout() {
  const location = useLocation()
  const navigate = useNavigate()
  const product = location.state?.product || {}
  const { pay } = usePaystack()

  const [form, setForm] = useState({
    schoolName: '', contactName: '', email: '', phone: '',
    quantity: 1, deliveryAddress: '', notes: '',
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const qty = Math.max(1, Number(form.quantity) || 1)
  const total = (product.price || 0) * qty

  function set(key) {
    return (e) => {
      setForm(f => ({ ...f, [key]: e.target.value }))
      if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }))
    }
  }

  function validate() {
    const errs = {}
    const schoolName      = validateSchoolName(form.schoolName, 'School Name')
    const contactName     = validateName(form.contactName, 'Contact Name')
    const email           = validateEmail(form.email)
    const phone           = validatePhone(form.phone)
    const quantity        = validateQuantity(form.quantity, 1, 20)
    const deliveryAddress = validateFreeText(form.deliveryAddress, 'Delivery Address', true, LIMITS.address)
    const notes           = validateFreeText(form.notes, 'Notes', false, LIMITS.notes)
    if (schoolName)      errs.schoolName      = schoolName
    if (contactName)     errs.contactName     = contactName
    if (email)           errs.email           = email
    if (phone)           errs.phone           = phone
    if (quantity)        errs.quantity        = quantity
    if (deliveryAddress) errs.deliveryAddress = deliveryAddress
    if (notes)           errs.notes           = notes
    return errs
  }

  async function handleSubmit(e) {
    e.preventDefault()

    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }

    setLoading(true)
    setError('')

    try {
      await pay({
        email: form.email.trim(),
        amount: total,
        metadata: {
          custom_fields: [
            { display_name: 'School',    variable_name: 'school',   value: sanitize(form.schoolName)    },
            { display_name: 'Contact',   variable_name: 'contact',  value: sanitize(form.contactName)   },
            { display_name: 'Phone',     variable_name: 'phone',    value: form.phone.trim()             },
            { display_name: 'Product',   variable_name: 'product',  value: product.solution              },
            { display_name: 'Quantity',  variable_name: 'quantity', value: String(qty)                   },
            { display_name: 'Delivery',  variable_name: 'delivery', value: sanitize(form.deliveryAddress) },
          ],
        },
        onSuccess: async (response) => {
          const apiUrl = import.meta.env.VITE_API_URL
          if (apiUrl) {
            try {
              await fetch(`${apiUrl}/orders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  type:               'hardware',
                  product_name:       product.solution,
                  amount:             total,
                  quantity:           qty,
                  customer_name:      sanitize(form.contactName),
                  customer_email:     form.email.trim(),
                  customer_phone:     form.phone.trim() || null,
                  school_name:        sanitize(form.schoolName) || null,
                  delivery_address:   sanitize(form.deliveryAddress) || null,
                  notes:              form.notes ? sanitize(form.notes) : null,
                  paystack_reference: response.reference,
                }),
              })
            } catch { /* silent — payment succeeded, order record is best-effort */ }
          }
          navigate('/order-success', { state: { reference: response.reference, amount: total, product: product.solution } })
        },
        onClose: () => setLoading(false),
      })
      setLoading(false)
    } catch (err) {
      setError(err.message || 'Could not open payment. Please try again.')
      setLoading(false)
    }
  }

  if (!product.solution) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-2xl font-display font-bold text-ink mb-4">No product selected</h2>
          <Link to="/products"><Button variant="primary">Browse Products</Button></Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen pt-8 pb-16 px-4">
      <SEO title="Order Hardware" description="" path="/product-checkout" noindex />
      <div className="max-w-4xl mx-auto">
        <Link to="/products" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-ink mb-8">
          <ArrowLeft size={16} /> Back to Products
        </Link>

        <div className="grid md:grid-cols-5 gap-8">
          {/* Form */}
          <div className="md:col-span-3 rounded border border-theme bg-surface p-6">
            <h2 className="text-2xl font-display font-bold text-ink mb-1">
              Order {product.solution}
            </h2>
            <p className="text-sm text-[var(--ink-soft)] mb-6">
              Fill in your details and pay securely via Paystack. We'll schedule delivery and installation.
            </p>

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1.5">School Name *</label>
                <input
                  type="text" required value={form.schoolName} onChange={set('schoolName')}
                  className="input"
                  style={errors.schoolName ? { borderColor: 'var(--warm-strong)' } : {}}
                  placeholder="e.g. Greenfield Academy"
                  maxLength={LIMITS.school}
                />
                <FieldError msg={errors.schoolName} />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1.5">Contact Name *</label>
                  <input
                    type="text" required value={form.contactName} onChange={set('contactName')}
                    className="input"
                    style={errors.contactName ? { borderColor: 'var(--warm-strong)' } : {}}
                    placeholder="Your full name"
                    maxLength={LIMITS.name}
                  />
                  <FieldError msg={errors.contactName} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1.5">Phone *</label>
                  <input
                    type="tel" required value={form.phone} onChange={set('phone')}
                    className="input"
                    style={errors.phone ? { borderColor: 'var(--warm-strong)' } : {}}
                    placeholder="+233 50 000 0000"
                    maxLength={LIMITS.phone}
                  />
                  <FieldError msg={errors.phone} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1.5">Email *</label>
                <input
                  type="email" required value={form.email} onChange={set('email')}
                  className="input"
                  style={errors.email ? { borderColor: 'var(--warm-strong)' } : {}}
                  placeholder="admin@yourschool.edu.gh"
                  maxLength={LIMITS.email}
                />
                <FieldError msg={errors.email} />
              </div>

              <div>
                <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1.5">Quantity</label>
                <input
                  type="number" min="1" max="20"
                  value={form.quantity} onChange={set('quantity')}
                  className="input w-28"
                  style={errors.quantity ? { borderColor: 'var(--warm-strong)' } : {}}
                />
                <FieldError msg={errors.quantity} />
              </div>

              <div>
                <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1.5">Delivery Address *</label>
                <textarea
                  required rows={3} value={form.deliveryAddress} onChange={set('deliveryAddress')}
                  className="input resize-none"
                  style={errors.deliveryAddress ? { borderColor: 'var(--warm-strong)' } : {}}
                  placeholder="School address for delivery and installation"
                  maxLength={LIMITS.address}
                />
                <FieldError msg={errors.deliveryAddress} />
              </div>

              <div>
                <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1.5">Notes (optional)</label>
                <textarea
                  rows={2} value={form.notes} onChange={set('notes')}
                  className="input resize-none"
                  style={errors.notes ? { borderColor: 'var(--warm-strong)' } : {}}
                  placeholder="Any special requirements?"
                  maxLength={LIMITS.notes}
                />
                <FieldError msg={errors.notes} />
              </div>

              {error && (
                <p className="text-xs rounded p-3" style={{ color: 'var(--warm-strong)', background: 'color-mix(in srgb, var(--warm) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--warm-strong) 30%, transparent)' }}>{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded btn-primary text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <><Loader2 size={16} className="animate-spin" /> Opening payment&hellip;</>
                ) : (
                  <><ShoppingCart size={16} /> Pay GHS {total.toLocaleString()} with Paystack</>
                )}
              </button>

              <p className="text-xs text-[var(--ink-soft)] text-center">
                Secure payment via Paystack
              </p>
            </form>
          </div>

          {/* Summary */}
          <div className="md:col-span-2 rounded border border-theme bg-surface p-6 h-fit sticky top-24">
            <h3 className="font-bold text-ink mb-4">Order Summary</h3>
            <div className="space-y-3 mb-6 text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-[var(--ink-soft)]">Product</span>
                <span className="font-medium text-ink text-right">{product.solution}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--ink-soft)]">Unit price</span>
                <span className="font-medium text-ink">GHS {(product.price || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--ink-soft)]">Quantity</span>
                <span className="font-medium text-ink">{qty}</span>
              </div>
            </div>
            <div className="border-t border-theme pt-4 mb-6 flex justify-between items-center">
              <span className="font-bold text-ink">Total</span>
              <span className="font-boxy text-2xl font-bold text-accent">GHS {total.toLocaleString()}</span>
            </div>
            <div className="space-y-2">
              {['Ongoing technical support'].map(b => (
                <div key={b} className="flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-accent mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-[var(--ink-soft)]">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
